import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Play, CheckCircle, AlertCircle, Camera, Footprints } from 'lucide-react';

const TutorialPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      id: 'overview',
      title: 'Welcome to HeelAI',
      subtitle: 'Right Leg Calf Raise Analysis',
      content: (
        <div className="space-y-6">
          <p className="text-zinc-300 text-lg">
            This tutorial will guide you through the proper setup and technique for accurate calf raise detection.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="glass rounded-xl p-4 text-center">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Camera className="text-cyan-400" size={24} />
              </div>
              <h4 className="font-bold text-white mb-1">AI-Powered</h4>
              <p className="text-sm text-zinc-400">Real-time pose detection</p>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="text-green-400" size={24} />
              </div>
              <h4 className="font-bold text-white mb-1">Form Tracking</h4>
              <p className="text-sm text-zinc-400">Validates proper technique</p>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Footprints className="text-purple-400" size={24} />
              </div>
              <h4 className="font-bold text-white mb-1">Rep Counting</h4>
              <p className="text-sm text-zinc-400">Automatic rep detection</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'positioning',
      title: 'Step 1: Camera Positioning',
      subtitle: 'Set up your exercise space',
      content: (
        <div className="space-y-6">
          <div className="glass rounded-xl p-6">
            <h4 className="font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="text-green-400" size={20} />
              Correct Setup
            </h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <ul className="space-y-3 text-zinc-300">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 font-bold">→</span>
                    Stand with your <strong className="text-cyan-400">RIGHT side</strong> facing the camera
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 font-bold">→</span>
                    Position camera at <strong className="text-cyan-400">hip height</strong> or slightly lower
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 font-bold">→</span>
                    Stand <strong className="text-cyan-400">6-10 feet</strong> from camera
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 font-bold">→</span>
                    Ensure your <strong className="text-cyan-400">full right leg</strong> is visible (hip to toe)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 font-bold">→</span>
                    Good lighting, <strong className="text-cyan-400">minimal shadows</strong>
                  </li>
                </ul>
              </div>
              <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                <div className="aspect-video relative flex items-center justify-center">
                  {/* Visual diagram */}
                  <svg viewBox="0 0 300 200" className="w-full h-full">
                    {/* Camera */}
                    <rect x="20" y="80" width="40" height="30" rx="5" fill="#06b6d4" />
                    <circle cx="40" cy="95" r="8" fill="#0e7490" />
                    <text x="40" y="125" textAnchor="middle" fill="#94a3b8" fontSize="10">Camera</text>
                    
                    {/* Person - side view stick figure */}
                    <circle cx="220" cy="50" r="15" fill="#a855f7" stroke="#c084fc" strokeWidth="2" />
                    <line x1="220" y1="65" x2="220" y2="110" stroke="#a855f7" strokeWidth="3" />
                    <line x1="220" y1="80" x2="200" y2="95" stroke="#a855f7" strokeWidth="3" />
                    <line x1="220" y1="110" x2="220" y2="160" stroke="#22c55e" strokeWidth="4" />
                    <circle cx="220" cy="130" r="4" fill="#22c55e" />
                    <line x1="220" y1="160" x2="220" y2="180" stroke="#22c55e" strokeWidth="4" />
                    <line x1="220" y1="180" x2="235" y2="180" stroke="#22c55e" strokeWidth="3" />
                    
                    {/* Arrow showing direction */}
                    <line x1="80" y1="95" x2="180" y2="95" stroke="#06b6d4" strokeWidth="2" strokeDasharray="5,5" />
                    <polygon points="175,90 185,95 175,100" fill="#06b6d4" />
                    
                    {/* Labels */}
                    <text x="220" y="195" textAnchor="middle" fill="#22c55e" fontSize="10" fontWeight="bold">Right Leg</text>
                    <text x="130" y="85" textAnchor="middle" fill="#94a3b8" fontSize="9">6-10 feet</text>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          <div className="glass rounded-xl p-6 border border-red-500/30 bg-red-500/5">
            <h4 className="font-bold text-red-400 mb-3 flex items-center gap-2">
              <AlertCircle size={20} />
              Avoid These Mistakes
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2 text-zinc-400">
                <span className="text-red-400">✗</span>
                Facing camera directly (front view)
              </div>
              <div className="flex items-start gap-2 text-zinc-400">
                <span className="text-red-400">✗</span>
                Left side facing camera
              </div>
              <div className="flex items-start gap-2 text-zinc-400">
                <span className="text-red-400">✗</span>
                Standing too close (only legs visible)
              </div>
              <div className="flex items-start gap-2 text-zinc-400">
                <span className="text-red-400">✗</span>
                Poor lighting or backlit
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'calibration',
      title: 'Step 2: Calibration',
      subtitle: 'Establishing your baseline position',
      content: (
        <div className="space-y-6">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <p className="text-yellow-400 font-medium">
              ⚠️ Calibration is critical for accurate detection. Follow these steps exactly.
            </p>
          </div>
          
          <div className="glass rounded-xl p-6">
            <h4 className="font-bold text-white mb-4">Calibration Position</h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-lg">
                  <div className="w-8 h-8 bg-cyan-500 text-black rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <div>
                    <p className="text-white font-medium">Stand on your RIGHT leg</p>
                    <p className="text-sm text-zinc-400">Left leg can be slightly lifted or touching for balance</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-lg">
                  <div className="w-8 h-8 bg-cyan-500 text-black rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <div>
                    <p className="text-white font-medium">Keep foot FLAT on floor</p>
                    <p className="text-sm text-zinc-400">Heel must be touching the ground</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-lg">
                  <div className="w-8 h-8 bg-cyan-500 text-black rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <div>
                    <p className="text-white font-medium">Stand completely STILL</p>
                    <p className="text-sm text-zinc-400">Don't move for ~1 second (20 frames)</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-lg">
                  <div className="w-8 h-8 bg-cyan-500 text-black rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                  <div>
                    <p className="text-white font-medium">Keep knee STRAIGHT</p>
                    <p className="text-sm text-zinc-400">Leg should be fully extended</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3 text-center">Correct Calibration Position</p>
                <div className="aspect-square relative flex items-center justify-center">
                  <svg viewBox="0 0 200 250" className="w-full h-full max-w-[200px]">
                    {/* Floor */}
                    <line x1="20" y1="230" x2="180" y2="230" stroke="#3f3f46" strokeWidth="3" />
                    
                    {/* Right leg - highlighted */}
                    <line x1="100" y1="100" x2="100" y2="160" stroke="#22c55e" strokeWidth="6" /> {/* Thigh */}
                    <circle cx="100" cy="160" r="5" fill="#22c55e" /> {/* Knee */}
                    <line x1="100" y1="160" x2="100" y2="215" stroke="#22c55e" strokeWidth="6" /> {/* Shin */}
                    <circle cx="100" cy="215" r="5" fill="#22c55e" /> {/* Ankle */}
                    <line x1="100" y1="215" x2="100" y2="230" stroke="#22c55e" strokeWidth="4" /> {/* Heel */}
                    <line x1="100" y1="230" x2="125" y2="230" stroke="#22c55e" strokeWidth="4" /> {/* Foot */}
                    
                    {/* Body outline */}
                    <ellipse cx="100" cy="60" rx="20" ry="25" fill="none" stroke="#a855f7" strokeWidth="2" /> {/* Head */}
                    <line x1="100" y1="85" x2="100" y2="100" stroke="#a855f7" strokeWidth="3" /> {/* Neck/torso */}
                    <ellipse cx="100" cy="100" rx="25" ry="5" fill="none" stroke="#a855f7" strokeWidth="2" /> {/* Hip */}
                    
                    {/* Annotations */}
                    <text x="140" y="160" fill="#22c55e" fontSize="10" fontWeight="bold">Knee straight</text>
                    <text x="140" y="230" fill="#22c55e" fontSize="10" fontWeight="bold">Heel on floor</text>
                    
                    {/* Checkmarks */}
                    <circle cx="85" cy="230" r="8" fill="#22c55e" />
                    <text x="85" y="234" textAnchor="middle" fill="white" fontSize="10">✓</text>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          <div className="glass rounded-xl p-4">
            <h4 className="text-white font-medium mb-2">What happens during calibration:</h4>
            <div className="grid md:grid-cols-3 gap-3 text-sm">
              <div className="bg-zinc-900/50 rounded-lg p-3">
                <p className="text-cyan-400 font-medium">Baseline Captured</p>
                <p className="text-zinc-400">Flat foot angle is recorded</p>
              </div>
              <div className="bg-zinc-900/50 rounded-lg p-3">
                <p className="text-cyan-400 font-medium">Position Locked</p>
                <p className="text-zinc-400">System locks onto right leg</p>
              </div>
              <div className="bg-zinc-900/50 rounded-lg p-3">
                <p className="text-cyan-400 font-medium">Ready Signal</p>
                <p className="text-zinc-400">Green banner appears</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'exercise',
      title: 'Step 3: Performing the Calf Raise',
      subtitle: 'Proper technique for detection',
      content: (
        <div className="space-y-6">
          <div className="glass rounded-xl p-6">
            <h4 className="font-bold text-white mb-4">The Movement Cycle</h4>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Phase 1: Start */}
              <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                <div className="text-center mb-3">
                  <span className="inline-block px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm font-bold">
                    Phase 1: DOWN
                  </span>
                </div>
                <div className="aspect-square relative flex items-center justify-center mb-3">
                  <svg viewBox="0 0 100 150" className="w-full h-full max-w-[100px]">
                    <line x1="50" y1="20" x2="50" y2="50" stroke="#f97316" strokeWidth="4" />
                    <circle cx="50" cy="50" r="3" fill="#f97316" />
                    <line x1="50" y1="50" x2="50" y2="110" stroke="#f97316" strokeWidth="4" />
                    <circle cx="50" cy="110" r="3" fill="#f97316" />
                    <line x1="50" y1="110" x2="50" y2="130" stroke="#f97316" strokeWidth="3" />
                    <line x1="50" y1="130" x2="70" y2="130" stroke="#f97316" strokeWidth="3" />
                    <line x1="30" y1="130" x2="90" y2="130" stroke="#3f3f46" strokeWidth="2" />
                  </svg>
                </div>
                <p className="text-sm text-zinc-400 text-center">Heel flat on floor, knee straight</p>
              </div>
              
              {/* Phase 2: Up */}
              <div className="bg-zinc-900 rounded-xl p-4 border-2 border-green-500/50">
                <div className="text-center mb-3">
                  <span className="inline-block px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-bold">
                    Phase 2: UP
                  </span>
                </div>
                <div className="aspect-square relative flex items-center justify-center mb-3">
                  <svg viewBox="0 0 100 150" className="w-full h-full max-w-[100px]">
                    <line x1="50" y1="10" x2="50" y2="40" stroke="#22c55e" strokeWidth="4" />
                    <circle cx="50" cy="40" r="3" fill="#22c55e" />
                    <line x1="50" y1="40" x2="50" y2="100" stroke="#22c55e" strokeWidth="4" />
                    <circle cx="50" cy="100" r="4" fill="#22c55e" />
                    <line x1="50" y1="100" x2="50" y2="115" stroke="#22c55e" strokeWidth="3" />
                    <line x1="50" y1="115" x2="65" y2="130" stroke="#22c55e" strokeWidth="3" />
                    <circle cx="65" cy="130" r="3" fill="#22c55e" />
                    <line x1="30" y1="130" x2="90" y2="130" stroke="#3f3f46" strokeWidth="2" />
                    {/* Up arrow */}
                    <path d="M 80 60 L 80 30 L 75 40 M 80 30 L 85 40" stroke="#22c55e" strokeWidth="2" fill="none" />
                  </svg>
                </div>
                <p className="text-sm text-green-400 text-center font-medium">Heel raised, on tiptoes</p>
              </div>
              
              {/* Phase 3: Return */}
              <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                <div className="text-center mb-3">
                  <span className="inline-block px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-bold">
                    Phase 3: REP ✓
                  </span>
                </div>
                <div className="aspect-square relative flex items-center justify-center mb-3">
                  <svg viewBox="0 0 100 150" className="w-full h-full max-w-[100px]">
                    <line x1="50" y1="20" x2="50" y2="50" stroke="#06b6d4" strokeWidth="4" />
                    <circle cx="50" cy="50" r="3" fill="#06b6d4" />
                    <line x1="50" y1="50" x2="50" y2="110" stroke="#06b6d4" strokeWidth="4" />
                    <circle cx="50" cy="110" r="3" fill="#06b6d4" />
                    <line x1="50" y1="110" x2="50" y2="130" stroke="#06b6d4" strokeWidth="3" />
                    <line x1="50" y1="130" x2="70" y2="130" stroke="#06b6d4" strokeWidth="3" />
                    <line x1="30" y1="130" x2="90" y2="130" stroke="#3f3f46" strokeWidth="2" />
                    {/* Down arrow */}
                    <path d="M 80 70 L 80 100 L 75 90 M 80 100 L 85 90" stroke="#06b6d4" strokeWidth="2" fill="none" />
                  </svg>
                </div>
                <p className="text-sm text-zinc-400 text-center">Lower heel → Rep counted!</p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="glass rounded-xl p-5">
              <h4 className="font-bold text-green-400 mb-3 flex items-center gap-2">
                <CheckCircle size={20} />
                Detection Criteria
              </h4>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  Foot angle ≥ 6° (toes higher than heel)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  Knee angle ≥ 155° (leg straight)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  Heel rises at least 1.5% of shin length
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  Hold at top briefly (~150ms)
                </li>
              </ul>
            </div>
            
            <div className="glass rounded-xl p-5">
              <h4 className="font-bold text-red-400 mb-3 flex items-center gap-2">
                <AlertCircle size={20} />
                Common Mistakes
              </h4>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex items-center gap-2">
                  <span className="text-red-400">✗</span>
                  Bending knee (squat motion)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-400">✗</span>
                  Not raising heel high enough
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-400">✗</span>
                  Moving too fast (bouncing)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-400">✗</span>
                  Shifting body position sideways
                </li>
              </ul>
            </div>
          </div>
          
          <div className="glass rounded-xl p-4 bg-cyan-500/5 border border-cyan-500/20">
            <p className="text-cyan-400 text-center">
              <strong>Tip:</strong> Move slowly and controlled. The system needs time to detect each phase of the movement.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'indicators',
      title: 'Step 4: Reading the Display',
      subtitle: 'Understanding the visual feedback',
      content: (
        <div className="space-y-6">
          <div className="glass rounded-xl p-6">
            <h4 className="font-bold text-white mb-4">Color Indicators</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <div className="w-8 h-8 bg-yellow-500 rounded-full mx-auto mb-2"></div>
                <p className="text-yellow-400 font-bold text-sm">Yellow</p>
                <p className="text-xs text-zinc-400">Calibrating</p>
              </div>
              <div className="text-center p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
                <div className="w-8 h-8 bg-cyan-500 rounded-full mx-auto mb-2"></div>
                <p className="text-cyan-400 font-bold text-sm">Cyan</p>
                <p className="text-xs text-zinc-400">Locked & Ready</p>
              </div>
              <div className="text-center p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                <div className="w-8 h-8 bg-green-500 rounded-full mx-auto mb-2"></div>
                <p className="text-green-400 font-bold text-sm">Green</p>
                <p className="text-xs text-zinc-400">Heel Raised</p>
              </div>
              <div className="text-center p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <div className="w-8 h-8 bg-red-500 rounded-full mx-auto mb-2"></div>
                <p className="text-red-400 font-bold text-sm">Red</p>
                <p className="text-xs text-zinc-400">Error/Issue</p>
              </div>
            </div>
          </div>
          
          <div className="glass rounded-xl p-6">
            <h4 className="font-bold text-white mb-4">Metrics Panel</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-xs text-zinc-500 uppercase mb-1">Foot Angle</p>
                <p className="text-2xl font-bold text-cyan-400">6°+</p>
                <p className="text-xs text-zinc-400 mt-1">Target: 6° or higher for "UP"</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-xs text-zinc-500 uppercase mb-1">Knee Angle</p>
                <p className="text-2xl font-bold text-green-400">155°+</p>
                <p className="text-xs text-zinc-400 mt-1">Must stay above 155°</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-4">
                <p className="text-xs text-zinc-500 uppercase mb-1">Heel Rise</p>
                <p className="text-2xl font-bold text-purple-400">1.5%+</p>
                <p className="text-xs text-zinc-400 mt-1">Of shin length</p>
              </div>
            </div>
          </div>
          
          <div className="glass rounded-xl p-6">
            <h4 className="font-bold text-white mb-4">Detection Status Panel</h4>
            <p className="text-zinc-400 mb-4">Shows which body parts are being tracked:</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                <CheckCircle size={14} /> Hip
              </span>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                <CheckCircle size={14} /> Knee
              </span>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                <CheckCircle size={14} /> Ankle
              </span>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                <CheckCircle size={14} /> Heel
              </span>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                <CheckCircle size={14} /> Toe
              </span>
            </div>
            <p className="text-sm text-zinc-500 mt-3">All 5 landmarks must be green for detection to work.</p>
          </div>
        </div>
      )
    },
    {
      id: 'ready',
      title: 'You\'re Ready!',
      subtitle: 'Start your exercise session',
      content: (
        <div className="space-y-6 text-center">
          <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="text-green-400" size={48} />
          </div>
          
          <h3 className="text-2xl font-bold text-white">You're all set!</h3>
          <p className="text-zinc-400 max-w-md mx-auto">
            Remember: Right side to camera, foot flat during calibration, then raise those heels!
          </p>
          
          <div className="glass rounded-xl p-6 max-w-md mx-auto">
            <h4 className="font-bold text-white mb-3">Quick Checklist</h4>
            <ul className="text-left space-y-2 text-zinc-300">
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={16} />
                Right side facing camera
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={16} />
                Full leg visible (hip to toe)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={16} />
                Good lighting
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={16} />
                Stand still with heel flat for calibration
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={16} />
                Wait for green "START" signal
              </li>
            </ul>
          </div>
          
          <button
            onClick={() => navigate('/exercise')}
            className="px-12 py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-lg rounded-full transition-transform active:scale-95 neon-cyan inline-flex items-center gap-3"
          >
            <Play size={24} fill="currentColor" />
            Start Exercise
          </button>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="fixed inset-0 carbon-texture pointer-events-none" />
      <div className="fixed top-1/4 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
      
      {/* Header */}
      <header className="relative z-10 p-6 flex items-center justify-between border-b border-zinc-800">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-white/5">
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gradient">TUTORIAL</h1>
            <p className="text-zinc-500 text-sm">How to use HeelAI</p>
          </div>
        </div>
        
        {/* Progress dots */}
        <div className="flex items-center gap-2">
          {steps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentStep(idx)}
              className={`w-3 h-3 rounded-full transition-colors ${
                idx === currentStep ? 'bg-cyan-500' : idx < currentStep ? 'bg-green-500' : 'bg-zinc-700'
              }`}
            />
          ))}
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="text-cyan-400 text-sm uppercase tracking-wider mb-2">
            Step {currentStep + 1} of {steps.length}
          </p>
          <h2 className="font-heading text-3xl font-bold text-white mb-2">
            {currentStepData.title}
          </h2>
          <p className="text-zinc-400">{currentStepData.subtitle}</p>
        </div>

        <div className="mb-12">
          {currentStepData.content}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={isFirstStep}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-colors ${
              isFirstStep 
                ? 'text-zinc-600 cursor-not-allowed' 
                : 'text-white hover:bg-white/5'
            }`}
          >
            <ArrowLeft size={20} />
            Previous
          </button>
          
          {!isLastStep ? (
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              className="flex items-center gap-2 px-8 py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full transition-colors"
            >
              Next
              <ArrowRight size={20} />
            </button>
          ) : (
            <button
              onClick={() => navigate('/exercise')}
              className="flex items-center gap-2 px-8 py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-full transition-colors"
            >
              <Play size={20} fill="currentColor" />
              Start Exercise
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default TutorialPage;
